/**
 * Follow-ups page. Bundled via import from app.js.
 * Props: api (API.json), toast, user, timezone, onCallLead(leadId),
 * onHumanCallLead(item) optional, canHumanCall optional boolean.
 * Without onHumanCallLead, "Call now" stays the existing AI call.
 * With it, "Call now" is the human browser call and "Have AI call" uses onCallLead.
 * Mark done is unchanged and is not tied to either call button.
 */
import { addDaysYmd, zonedYmd, zoneLabel } from './datetime.js';

const { useState, useEffect } = React;

const TABS = [
  ['overdue', 'Overdue'],
  ['today', 'Today'],
  ['upcoming', 'Upcoming'],
  ['done', 'Done']
];
const INTEREST = { hot: 'Hot', warm: 'Warm', cold: 'Cold', not_interested: 'Not interested' };
const DOW = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const EMPTY = {
  overdue: 'Nothing is overdue. You are caught up.',
  today: 'Nothing else is due today.',
  upcoming: 'No upcoming follow-ups yet. When someone asks for a call at a certain time, it shows up here.',
  done: 'No completed follow-ups yet.'
};

function errorMessage(err, fallback) {
  const data = err && err.data;
  return (data && (data.message || data.detail)) || (err && err.message) || fallback;
}

function ymdInZone(date, timeZone) {
  const ymd = zonedYmd(date, timeZone);
  let weekday = '';
  try {
    weekday = new Intl.DateTimeFormat('en-US', { timeZone: timeZone, weekday: 'short' }).format(date);
  } catch (e) {
    weekday = '';
  }
  return { ymd: ymd, weekday: weekday };
}

function addYmd(ymd, days) {
  return addDaysYmd(ymd, days);
}

function mondayYmd(date, timeZone) {
  const parts = ymdInZone(date, timeZone);
  const dow = DOW[parts.weekday] || 0;
  return addYmd(parts.ymd, dow === 0 ? -6 : 1 - dow);
}

function formatYmd(ymd, options) {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', Object.assign({ timeZone: 'UTC' }, options)).format(new Date(Date.UTC(y, m - 1, d, 12)));
}

function formatDue(iso, timeZone) {
  if (!iso) return 'No time set';
  const due = new Date(iso);
  if (Number.isNaN(due.getTime())) return 'No time set';
  const time = new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', minute: '2-digit' }).format(due);
  const dueKey = ymdInZone(due, timeZone).ymd;
  const todayKey = ymdInZone(new Date(), timeZone).ymd;
  if (dueKey === todayKey) return 'Today ' + time;
  if (dueKey === addYmd(todayKey, 1)) return 'Tomorrow ' + time;
  return new Intl.DateTimeFormat('en-US', {
    timeZone, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
  }).format(due);
}

function formatPhone(value) {
  const match = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(value || '');
  return match ? '(' + match[1] + ') ' + match[2] + '-' + match[3] : (value || '');
}

function snippet(text) {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (!clean) return '';
  return clean.length > 180 ? clean.slice(0, 177) + '…' : clean;
}

const AGENT_SEAT_ROLES = ['tenant_admin', 'supervisor', 'agent', 'super_admin'];
const HUMAN_CALL_TITLE = 'Calls from your browser. You talk to them.';
const NO_SEAT_HINT = 'Browser calling needs an agent seat. You can still have the AI call.';

function userHasAgentSeat(user) {
  if (!user || AGENT_SEAT_ROLES.indexOf(user.role) < 0) return false;
  const tenantId = user.tenant_id != null && user.tenant_id !== '' ? user.tenant_id : user.tenantId;
  const userId = user.id;
  if (tenantId == null || tenantId === '' || userId == null || userId === '') return false;
  return /^[A-Za-z0-9_]+$/.test('t' + tenantId + '_u' + userId);
}

function resolveCallMode(user, onHumanCallLead, canHumanCall) {
  if (typeof onHumanCallLead !== 'function') return 'legacy';
  if (canHumanCall === true) return 'human';
  if (canHumanCall === false) return 'ai';
  if (!user) return 'legacy';
  return userHasAgentSeat(user) ? 'human' : 'ai';
}

function PhoneIcon() {
  return (
    <svg className="fu-phone-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
    </svg>
  );
}

function toLocalInput(iso) {
  if (!iso) return '';
  const due = new Date(iso);
  if (Number.isNaN(due.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return due.getFullYear() + '-' + p(due.getMonth() + 1) + '-' + p(due.getDate()) + 'T' + p(due.getHours()) + ':' + p(due.getMinutes());
}

function absoluteFeed(pathOrUrl) {
  if (!pathOrUrl) return '';
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const base = (typeof window !== 'undefined' && window.API_BASE)
    ? String(window.API_BASE).replace(/\/$/, '')
    : (typeof window !== 'undefined' ? window.location.origin : '');
  return base + (String(pathOrUrl).charAt(0) === '/' ? pathOrUrl : '/' + pathOrUrl);
}

function downloadIcs(ics, filename) {
  const blob = new Blob([ics], { type: 'text/calendar' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'follow-up.ics';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function FollowUpActions({ item, readOnly, busy, users, rescheduleId, rescheduleValue, setRescheduleId, setRescheduleValue, menuId, links, callMode, onCall, onAiCall, onDone, onAssign, onSaveTime, onCalendar, onDownload, timeZone }) {
  if (readOnly) return null;
  const open = rescheduleId === item.id;
  const phone = item.lead_phone || item.phone_e164 || '';
  const canHuman = !!(item.lead_id || phone);
  const human = callMode === 'human';
  const aiOnly = callMode === 'ai';
  const noteId = 'fu-call-note-' + item.id;
  return (
    <div>
      <div className="fu-actions">
        {human ? (
          <button type="button" className="fu-btn fu-btn--primary fu-btn--call" disabled={busy || !canHuman} title={HUMAN_CALL_TITLE} aria-describedby={noteId} onClick={() => onCall(item)}>
            <PhoneIcon />
            Call now
          </button>
        ) : null}
        {human ? (
          <button type="button" className="fu-btn" disabled={busy || !item.lead_id} title="Starts an AI call to this lead." onClick={() => onAiCall(item)}>Have AI call</button>
        ) : null}
        {aiOnly ? (
          <button type="button" className="fu-btn fu-btn--primary" disabled={busy || !item.lead_id} title="Starts an AI call to this lead." aria-describedby={noteId} onClick={() => onAiCall(item)}>Have AI call</button>
        ) : null}
        {!human && !aiOnly ? (
          <button type="button" className="fu-btn fu-btn--primary" disabled={busy || !item.lead_id} onClick={() => onCall(item)}>Call now</button>
        ) : null}
        <button type="button" className="fu-btn" disabled={busy} onClick={() => { setRescheduleId(item.id); setRescheduleValue(toLocalInput(item.due_at)); }}>Reschedule</button>
        <button type="button" className="fu-btn" disabled={busy} onClick={() => onDone(item)}>Mark done</button>
        {Array.isArray(users) ? (
          <select className="fu-select" aria-label="Assign teammate" disabled={busy} value={item.assigned_user_id || ''} onChange={(e) => onAssign(item, e.target.value)}>
            <option value="">Unassigned</option>
            {item.assigned_user_id && !users.some((user) => String(user.id) === String(item.assigned_user_id)) ? <option value={item.assigned_user_id}>Assigned teammate</option> : null}
            {users.map((user) => <option key={user.id} value={user.id}>{user.name || user.email}</option>)}
          </select>
        ) : null}
        <button type="button" className="fu-btn" disabled={busy} onClick={() => onCalendar(item)}>Add to calendar</button>
      </div>
      {human ? <p id={noteId} className="fu-hint fu-call-note">{HUMAN_CALL_TITLE}</p> : null}
      {aiOnly ? <p id={noteId} className="fu-hint fu-call-note">{NO_SEAT_HINT}</p> : null}
      {open ? (
        <form className="fu-reschedule" onSubmit={(e) => { e.preventDefault(); onSaveTime(item); }}>
          <input className="fu-input" type="datetime-local" required value={rescheduleValue} onChange={(e) => setRescheduleValue(e.target.value)} aria-label="New follow-up time" />
          <button type="submit" className="fu-btn fu-btn--primary" disabled={busy}>Save time</button>
          <button type="button" className="fu-btn" onClick={() => setRescheduleId(null)}>Cancel</button>
          <span className="fu-hint">This picker uses your computer's clock. The list shows {zoneLabel(timeZone)}.</span>
        </form>
      ) : null}
      {menuId === item.id && links ? (
        <div className="fu-menu">
          <a href={links.google_url} target="_blank" rel="noopener noreferrer">Google Calendar</a>
          <a href={links.outlook_url} target="_blank" rel="noopener noreferrer">Outlook</a>
          <button type="button" className="fu-btn" onClick={() => onDownload(links.ics)}>Download .ics</button>
        </div>
      ) : null}
    </div>
  );
}

function FollowUpBody({ item, timeZone, children }) {
  const name = item.lead_name || item.caller_name || 'Unknown caller';
  const phone = item.lead_phone || item.phone_e164 || '';
  const interest = INTEREST[String(item.call_interest || '').toLowerCase()] ? String(item.call_interest).toLowerCase() : '';
  const step = item.summary || item.reason || item.note || '';
  const past = item.due_at && new Date(item.due_at).getTime() < Date.now();
  return (
    <article className={'fu-card' + (past && item.status !== 'done' ? ' fu-card--overdue' : '')}>
      <div className="fu-card-top">
        <div>
          <div className="fu-name">{name}{item.lead_company ? <span className="fu-company"> · {item.lead_company}</span> : null}</div>
          {phone ? <a className="fu-phone" href={'tel:' + phone}>{formatPhone(phone)}</a> : null}
        </div>
        <div className="fu-badges">
          <span className="fu-badge fu-badge--kind">{item.kind === 'callback' ? 'Callback' : 'Follow-up'}</span>
          {interest ? <span className={'fu-badge fu-badge--' + interest}>{INTEREST[interest]}</span> : null}
          <span className="fu-when">{formatDue(item.due_at, timeZone)}</span>
        </div>
      </div>
      {step ? <p className="fu-snippet">{step}</p> : null}
      {item.preferred_time ? <p className="fu-said">Caller said “{item.preferred_time}”</p> : null}
      {item.call_summary ? <p className="fu-snippet">{snippet(item.call_summary)}</p> : null}
      {children}
    </article>
  );
}

export function FollowUpsView({ api, toast, user, timezone, onCallLead, onHumanCallLead, canHumanCall }) {
  const [serverZone, setServerZone] = useState('');
  const tz = serverZone || timezone || 'America/Los_Angeles';
  const readOnly = !!(user && user.role === 'viewer');
  const [tab, setTab] = useState('today');
  const [view, setView] = useState('list');
  const [weekTouched, setWeekTouched] = useState(false);
  const [weekYmd, setWeekYmd] = useState(() => mondayYmd(new Date(), timezone || 'America/Los_Angeles'));
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ overdue: 0, today: 0, upcoming: 0 });
  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [menuId, setMenuId] = useState(null);
  const [links, setLinks] = useState(null);
  const [rescheduleId, setRescheduleId] = useState(null);
  const [rescheduleValue, setRescheduleValue] = useState('');
  const [feed, setFeed] = useState('');
  const [feedBusy, setFeedBusy] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!weekTouched) setWeekYmd(mondayYmd(new Date(), tz));
  }, [tz, weekTouched]);

  useEffect(() => {
    let cancelled = false;
    api('GET', '/api/users')
      .then((result) => { if (!cancelled) setUsers((result && result.users) || []); })
      .catch(() => { if (!cancelled) setUsers(null); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    let path = '/api/callbacks?';
    if (view === 'week') {
      path += 'status=open&from=' + encodeURIComponent(weekYmd) + '&to=' + encodeURIComponent(addYmd(weekYmd, 7));
    } else if (tab === 'done') {
      path += 'status=done';
    } else {
      path += 'status=open&bucket=' + encodeURIComponent(tab);
    }
    Promise.all([api('GET', '/api/callbacks/summary'), api('GET', path)])
      .then(([summary, data]) => {
        if (cancelled) return;
        setCounts({ overdue: Number(summary.overdue) || 0, today: Number(summary.today) || 0, upcoming: Number(summary.upcoming) || 0 });
        if (summary.timezone) setServerZone(summary.timezone);
        setItems((data && data.items) || []);
        setMenuId(null);
      })
      .catch((err) => { if (!cancelled) setError(errorMessage(err, 'Could not load follow-ups.')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tab, view, weekYmd, tick]);

  function notify(message, type) { if (toast) toast(message, type); }
  function reload() { setTick((n) => n + 1); }

  async function markDone(item) {
    setBusyId(item.id);
    try {
      await api('POST', '/api/callbacks/' + encodeURIComponent(item.id) + '/done', {});
      notify('Marked done');
      reload();
    } catch (err) {
      notify(errorMessage(err, 'Could not mark this done.'), 'error');
    } finally { setBusyId(null); }
  }

  async function assign(item, value) {
    setBusyId(item.id);
    try {
      await api('PATCH', '/api/callbacks/' + encodeURIComponent(item.id), { assigned_user_id: value ? Number(value) : null });
      notify(value ? 'Assigned' : 'Unassigned');
      reload();
    } catch (err) {
      notify(errorMessage(err, 'Could not assign this follow-up.'), 'error');
    } finally { setBusyId(null); }
  }

  async function saveTime(item) {
    const when = new Date(rescheduleValue);
    if (Number.isNaN(when.getTime())) { notify('Enter a valid date and time.', 'error'); return; }
    setBusyId(item.id);
    try {
      await api('PATCH', '/api/callbacks/' + encodeURIComponent(item.id), { due_at: when.toISOString() });
      notify('Time updated');
      setRescheduleId(null);
      reload();
    } catch (err) {
      notify(errorMessage(err, 'Could not reschedule.'), 'error');
    } finally { setBusyId(null); }
  }

  async function openCalendar(item) {
    if (menuId === item.id) { setMenuId(null); return; }
    setBusyId(item.id);
    try {
      const data = await api('GET', '/api/callbacks/' + encodeURIComponent(item.id) + '/calendar-links');
      setLinks(data);
      setMenuId(item.id);
    } catch (err) {
      notify(errorMessage(err, 'Could not build a calendar link.'), 'error');
    } finally { setBusyId(null); }
  }

  async function createFeed() {
    setFeedBusy(true);
    try {
      const data = await api('POST', '/api/calendar/feed-token', {});
      setFeed(absoluteFeed(data.url || data.path));
      notify('Calendar link ready');
    } catch (err) {
      notify(errorMessage(err, 'Could not create a calendar link.'), 'error');
    } finally { setFeedBusy(false); }
  }

  async function copyFeed() {
    try {
      await navigator.clipboard.writeText(feed);
      notify('Calendar link copied');
    } catch {
      notify('Select the link and copy it.', 'error');
    }
  }

  function callAi(item) {
    if (!item.lead_id) { notify('This follow-up has no lead to call.', 'error'); return; }
    if (onCallLead) onCallLead(item.lead_id);
  }

  function callHuman(item) {
    const phone = item.lead_phone || item.phone_e164 || '';
    if (!item.lead_id && !phone) { notify('This follow-up has no lead to call.', 'error'); return; }
    if (onHumanCallLead) onHumanCallLead(item);
  }

  const callMode = resolveCallMode(user, onHumanCallLead, canHumanCall);
  const weekDays = [0, 1, 2, 3, 4, 5, 6].map((offset) => addYmd(weekYmd, offset));
  const todayKey = ymdInZone(new Date(), tz).ymd;
  const actionProps = {
    readOnly, users, rescheduleId, rescheduleValue, setRescheduleId, setRescheduleValue, menuId, links, timeZone: tz,
    callMode, onCall: callMode === 'human' ? callHuman : callAi, onAiCall: callAi,
    onDone: markDone, onAssign: assign, onSaveTime: saveTime, onCalendar: openCalendar,
    onDownload: (ics) => downloadIcs(ics, 'follow-up.ics')
  };

  return (
    <div className="fu">
      <header className="fu-header">
        <div>
          <h1 className="fu-title">Follow-ups</h1>
          <p className="fu-sub">When a caller asks for a time — or sounds ready to talk — it lands here, on the day it is due. Times shown in {zoneLabel(tz)}.</p>
        </div>
        <div className="fu-counts">
          {['overdue', 'today', 'upcoming'].map((key) => (
            <button key={key} type="button" className={'fu-count fu-count--' + key + (tab === key && view === 'list' ? ' fu-count--active' : '')} onClick={() => { setTab(key); setView('list'); }}>
              <strong>{counts[key]}</strong>
              <span>{key === 'overdue' ? 'Overdue' : key === 'today' ? 'Today' : 'Upcoming'}</span>
            </button>
          ))}
        </div>
      </header>
      <div className="fu-toolbar">
        <div className="fu-tabs" role="tablist">
          {TABS.map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id && view === 'list'} className={'fu-tab' + (tab === id && view === 'list' ? ' fu-tab--active' : '')} onClick={() => { setTab(id); setView('list'); }}>{label}</button>
          ))}
        </div>
        <div className="fu-views">
          <button type="button" className={'fu-btn' + (view === 'list' ? ' fu-tab--active' : '')} onClick={() => setView('list')}>List</button>
          <button type="button" className={'fu-btn' + (view === 'week' ? ' fu-tab--active' : '')} onClick={() => setView('week')}>Week</button>
        </div>
      </div>
      {error ? <div className="fu-error">{error}</div> : null}
      {loading ? <div className="fu-empty">Loading follow-ups…</div> : null}
      {!loading && view === 'list' && !items.length && !error ? <div className="fu-empty">{EMPTY[tab]}</div> : null}
      {!loading && view === 'list' && items.length ? (
        <div className="fu-list">
          {items.map((item) => (
            <FollowUpBody key={item.id} item={item} timeZone={tz}>
              <FollowUpActions item={item} busy={busyId === item.id} {...actionProps} />
            </FollowUpBody>
          ))}
        </div>
      ) : null}
      {!loading && view === 'week' ? (
        <section>
          <div className="fu-week-head">
            <div className="fu-week-nav">
              <button type="button" className="fu-btn" onClick={() => { setWeekTouched(true); setWeekYmd(addYmd(weekYmd, -7)); }}>Previous week</button>
              <button type="button" className="fu-btn" onClick={() => { setWeekTouched(false); setWeekYmd(mondayYmd(new Date(), tz)); }}>This week</button>
              <button type="button" className="fu-btn" onClick={() => { setWeekTouched(true); setWeekYmd(addYmd(weekYmd, 7)); }}>Next week</button>
            </div>
            <div className="fu-meta">{formatYmd(weekYmd, { month: 'short', day: 'numeric' })} – {formatYmd(addYmd(weekYmd, 6), { month: 'short', day: 'numeric' })}</div>
          </div>
          {!items.length && !error ? <div className="fu-empty">Nothing scheduled this week. Check another week, or open Overdue for anything that slipped.</div> : null}
          <div className="fu-week">
            {weekDays.map((day) => {
              const dayItems = items.filter((item) => item.due_at && ymdInZone(new Date(item.due_at), tz).ymd === day);
              return (
                <div key={day} className={'fu-day' + (day === todayKey ? ' fu-day--today' : '')}>
                  <div className="fu-day-label"><strong>{formatYmd(day, { weekday: 'short' })}</strong><span>{formatYmd(day, { month: 'short', day: 'numeric' })}</span></div>
                  <div className="fu-day-items">
                    {dayItems.map((item) => (
                      <div key={item.id} className="fu-mini">
                        <div className="fu-when">{formatDue(item.due_at, tz)}</div>
                        <div className="fu-name">{item.lead_name || item.caller_name || 'Unknown caller'}</div>
                        {item.lead_company ? <div className="fu-company">{item.lead_company}</div> : null}
                        <FollowUpActions item={item} busy={busyId === item.id} {...actionProps} />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}
      <section className="fu-subscribe">
        <h2>Subscribe in Google or Outlook</h2>
        <p>In Google Calendar choose Other calendars, then From URL. In Outlook choose Add calendar, then Subscribe from web. The link shows open follow-ups assigned to you, plus any that nobody has taken yet.</p>
        {readOnly ? <p>You can review follow-ups. An admin can create the calendar link.</p> : (
          <button type="button" className="fu-btn fu-btn--primary" disabled={feedBusy} onClick={createFeed}>{feed ? 'Create a new link' : 'Create calendar link'}</button>
        )}
        {feed ? (
          <div>
            <div className="fu-url-row">
              <input className="fu-input fu-url" readOnly value={feed} aria-label="Calendar feed URL" onFocus={(e) => e.target.select()} />
              <button type="button" className="fu-btn" onClick={copyFeed}>Copy</button>
            </div>
            <p className="fu-hint">Creating a new link turns off the previous one.</p>
          </div>
        ) : null}
      </section>
    </div>
  );
}
